import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import _, { isFinite, isString } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { CustomerPortalApi } from 'api/customer-portal/api';
import { CustomerPortalSettingsApi, CustomerPortalSettingsAlgorithm, CustomerPortalSettingsMechanism, CustomerPortalInviteMethod } from 'api/customer-portal/settings-api';
import { CustomerPortalWidgetType } from 'api/customer-portal/types';
import { ProjectAPI } from 'api/project';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { getDynamicConditionFields } from 'data/templates/projects/utils';
import { useIsCustomerPortalEnabled } from 'hooks/use-feature';
import { useTranslate } from 'hooks/use-translation';
import { db } from 'workers/db';
export const useGetAllCustomerPortalsLight = ()=>{
    return useQuery([
        'useGetAllCustomerPortalsLight'
    ], ()=>CustomerPortalApi.getCustomerPortalsInAccount());
};
export const useGetCustomerPortalById = (portalId)=>{
    return useQuery([
        'useGetCustomerPortalById',
        portalId
    ], ()=>CustomerPortalApi.getCustomerPortalById(portalId), {
        enabled: _.isFinite(portalId)
    });
};
export const useGetCustomerPortalByIdIDB = (portalId)=>{
    return useQuery([
        'useGetCustomerPortalByIdIDB',
        portalId
    ], async ()=>{
        const arr = await db.customerPortal.where({
            customerPortalId: portalId
        }).toArray();
        return JSON.parse(JSON.stringify(arr === null || arr === void 0 ? void 0 : arr[0]));
    }, {
        enabled: _.isFinite(portalId)
    });
};
export const useGetCustomerPortalByIdFromIDB = (portalId)=>{
    var _i_data, _q_data;
    const q = useGetCustomerPortalById(portalId);
    const i = useGetCustomerPortalByIdIDB(portalId);
    const iDate = moment(i === null || i === void 0 ? void 0 : (_i_data = i.data) === null || _i_data === void 0 ? void 0 : _i_data.updatedAt);
    const qDate = moment(q === null || q === void 0 ? void 0 : (_q_data = q.data) === null || _q_data === void 0 ? void 0 : _q_data.updatedAt);
    if ((i === null || i === void 0 ? void 0 : i.isLoading) && (q === null || q === void 0 ? void 0 : q.isLoading)) {
        return {
            query: i,
            isDirty: false
        };
    }
    // If the portal is not available in IDB
    if (!(i === null || i === void 0 ? void 0 : i.data)) {
        return {
            query: q,
            isDirty: false
        };
    }
    // In some case the CP is deleted
    if (!(q === null || q === void 0 ? void 0 : q.data)) {
        return {
            query: q,
            isDirty: false
        };
    }
    const isAfter = iDate.isAfter(qDate);
    if (isAfter) {
        return {
            query: i,
            isDirty: true
        };
    }
    return {
        query: q,
        isDirty: false
    };
};
export const useCreateCustomerPortal = ()=>{
    return useMutation((p)=>CustomerPortalApi.createCustomerPortal(p));
};
export const useUpdateCustomerPortal = ()=>{
    return useMutation((p)=>{
        return CustomerPortalApi.updateCustomerPortal(p.portalId, p.payload);
    });
};
export const useDeleteCustomerPortal = ()=>{
    return useMutation((p)=>CustomerPortalApi.deleteCustomerPortalById(p));
};
export const useGetCustomerPortalForPreview = ({ portalId, projectId })=>{
    const query = useQuery([
        'useGetCustomerPortalForPreview',
        projectId,
        portalId
    ], ()=>CustomerPortalApi.getCustomerPortalForProjectForPreview({
            projectId,
            portalId
        }));
    return query;
};
export const useGetPortalForProject = (projectId)=>{
    return useQuery([
        'useGetPortalForProject',
        projectId
    ], ()=>CustomerPortalApi.getPortalForProject(projectId));
};
export const useGetInputFieldsForWidget = (payload)=>{
    // if (!payload) {
    //   return useQuery(['widget-input-fields'], () => Promise.resolve(null), {
    //     enabled: false,
    //   });
    // }
    return useQuery([
        'widget-input-fields',
        payload === null || payload === void 0 ? void 0 : payload.widgetType,
        payload === null || payload === void 0 ? void 0 : payload.widgetUuid
    ], async ()=>{
        if (!payload) {
            return null;
        }
        if ((payload === null || payload === void 0 ? void 0 : payload.widgetType) === CustomerPortalWidgetType.LdsWidget) {
            return [];
        }
        return payload ? CustomerPortalApi.getInputFields(payload) : null;
    }, {
        enabled: !!payload
    });
};
export const useGetEmbedSettings = ()=>{
    return useQuery([
        'useGetEmbedSettings'
    ], ()=>CustomerPortalSettingsApi.getEmbedSettings());
};
export const useUpdateEmbedSettings = ()=>{
    const { refetch } = useGetEmbedSettings();
    return useMutation((p)=>CustomerPortalSettingsApi.updateEmbedSettings(p), {
        onSuccess: ()=>{
            refetch();
        }
    });
};
export const useGetInviteSettings = ()=>{
    return useQuery([
        'useGetInviteSettings'
    ], ()=>CustomerPortalSettingsApi.getInviteSettings());
};
export const useIsRTEEnabledInCpMultiLineText = ()=>{
    return useQuery([
        'useIsRTEEnabledInCpMultiLineText'
    ], ()=>CustomerPortalSettingsApi.isRTEEnabledInCpMultiLineText());
};
export const useUpdateInviteSettings = ()=>{
    return useMutation((p)=>CustomerPortalSettingsApi.updateInviteSettings(p));
};
export const useGetCodeBlock = ()=>{
    return useQuery([
        'useGetCodeBlock'
    ], ()=>CustomerPortalSettingsApi.getCodeBlock(), {
        staleTime: Infinity
    });
};
export const useCanInvite = ()=>{
    var _data_inviteMethodMeta;
    const { data } = useGetInviteSettings();
    const isCpEnabled = useIsCustomerPortalEnabled();
    const canInvite = useMemo(()=>{
        if (!isCpEnabled) {
            return true;
        }
        if ((data === null || data === void 0 ? void 0 : data.inviteMethod) === CustomerPortalInviteMethod.AnyoneWithTheLink) {
            return true;
        }
        if ((data === null || data === void 0 ? void 0 : data.inviteMethod) === CustomerPortalInviteMethod.EmailsFromCustomerDomainsAndAdditionalDomains) {
            var _data_inviteMethodMeta;
            return data === null || data === void 0 ? void 0 : (_data_inviteMethodMeta = data.inviteMethodMeta) === null || _data_inviteMethodMeta === void 0 ? void 0 : _data_inviteMethodMeta.userWithPermissionCanInviteCustomers;
        }
        return false;
    }, [
        data === null || data === void 0 ? void 0 : data.inviteMethod,
        data === null || data === void 0 ? void 0 : (_data_inviteMethodMeta = data.inviteMethodMeta) === null || _data_inviteMethodMeta === void 0 ? void 0 : _data_inviteMethodMeta.userWithPermissionCanInviteCustomers,
        isCpEnabled
    ]);
    return canInvite;
};
export const useGetDynamicConditionFieldsUsedInPortal = (payload)=>{
    var _payload_portal;
    return useQuery([
        'useGetDynamicConditionFieldsUsedInPortal',
        payload === null || payload === void 0 ? void 0 : (_payload_portal = payload.portal) === null || _payload_portal === void 0 ? void 0 : _payload_portal.customerPortalId
    ], async ()=>{
        const d = await CustomerPortalApi.getDynamicConditionFieldsUsedInPortal(payload);
        if (d) {
            var _d_reduce, _iframe_contentWindow;
            const map = d === null || d === void 0 ? void 0 : (_d_reduce = d.reduce) === null || _d_reduce === void 0 ? void 0 : _d_reduce.call(d, (acc, z)=>{
                if ((z === null || z === void 0 ? void 0 : z.fieldType) === FieldTypes.MultiSelect) {
                    if (isString(z === null || z === void 0 ? void 0 : z.fieldValue)) {
                        var _z_fieldValue_split_map, _z_fieldValue_split_map1, _z_fieldValue_split, _z_fieldValue_split1, _z_fieldValue;
                        z.fieldValue = z === null || z === void 0 ? void 0 : (_z_fieldValue = z.fieldValue) === null || _z_fieldValue === void 0 ? void 0 : (_z_fieldValue_split1 = _z_fieldValue.split) === null || _z_fieldValue_split1 === void 0 ? void 0 : (_z_fieldValue_split = _z_fieldValue_split1.call(_z_fieldValue, ',')) === null || _z_fieldValue_split === void 0 ? void 0 : (_z_fieldValue_split_map1 = _z_fieldValue_split.map) === null || _z_fieldValue_split_map1 === void 0 ? void 0 : (_z_fieldValue_split_map = _z_fieldValue_split_map1.call(_z_fieldValue_split, Number)) === null || _z_fieldValue_split_map === void 0 ? void 0 : _z_fieldValue_split_map.filter(isFinite);
                    }
                }
                acc[z === null || z === void 0 ? void 0 : z.fieldName] = z === null || z === void 0 ? void 0 : z.fieldValue;
                return acc;
            }, {});
            const fromDb = await db.liveCustomerPortal.where('id').equals('new').first();
            if (fromDb) {
                await db.liveCustomerPortal.update('new', {
                    currentPage: fromDb === null || fromDb === void 0 ? void 0 : fromDb.currentPage,
                    portal: fromDb === null || fromDb === void 0 ? void 0 : fromDb.portal,
                    projectId: fromDb === null || fromDb === void 0 ? void 0 : fromDb.projectId,
                    id: 'new',
                    contextValues: map
                });
            } else {
                var _ref;
                var _payload_portal_pages_filter_, _payload_portal_pages_filter, _payload_portal_pages, _payload_portal;
                await db.liveCustomerPortal.put({
                    currentPage: payload === null || payload === void 0 ? void 0 : (_payload_portal = payload.portal) === null || _payload_portal === void 0 ? void 0 : (_payload_portal_pages = _payload_portal.pages) === null || _payload_portal_pages === void 0 ? void 0 : (_payload_portal_pages_filter = _payload_portal_pages.filter((p)=>p.pageType === 'HOME')) === null || _payload_portal_pages_filter === void 0 ? void 0 : (_payload_portal_pages_filter_ = _payload_portal_pages_filter[0]) === null || _payload_portal_pages_filter_ === void 0 ? void 0 : _payload_portal_pages_filter_.pageReferenceId,
                    portal: payload === null || payload === void 0 ? void 0 : payload.portal,
                    projectId: (_ref = payload === null || payload === void 0 ? void 0 : payload.projectId) !== null && _ref !== void 0 ? _ref : -1,
                    id: 'new',
                    contextValues: map
                });
            }
            const iframe = document.getElementById('cp-live-preview');
            iframe === null || iframe === void 0 ? void 0 : (_iframe_contentWindow = iframe.contentWindow) === null || _iframe_contentWindow === void 0 ? void 0 : _iframe_contentWindow.postMessage({
                type: 'cp-conditions-update',
                payload: map
            }, '*');
        }
        return d;
    }, {});
};
export const useGetLivePreview = ()=>{
    return useQuery([
        'useGetCustomerPortalForPreviewInBuilder, live'
    ], async ()=>{
        const x = await db.liveCustomerPortal.where('id').equals('new').first();
        const resp = await CustomerPortalApi.getCustomerPortalForPreview({
            portal: x === null || x === void 0 ? void 0 : x.portal,
            contextValues: x === null || x === void 0 ? void 0 : x.contextValues,
            projectId: x === null || x === void 0 ? void 0 : x.projectId
        });
        return {
            ...resp !== null && resp !== void 0 ? resp : {},
            currentPage: x === null || x === void 0 ? void 0 : x.currentPage
        };
    }, {
        refetchOnMount: true
    });
};
export const useGetCustomerPortalSettingsAlgorithmOptions = ()=>{
    const translate = useTranslate();
    return Object.values(CustomerPortalSettingsAlgorithm).map((key)=>({
            value: key,
            label: translate(`customer_portal_settings.algorithm.${key}.label`)
        }));
};
export const useGetCustomerPortalMechanismOptions = ()=>{
    const translate = useTranslate();
    return Object.values(CustomerPortalSettingsMechanism).map((key)=>({
            value: key,
            label: translate(`customer_portal_settings.mechanism.${key}.label`),
            hint: translate(`customer_portal_settings.mechanism.${key}.description`)
        }));
};
export const useGetAllowedWidgetsForPageType = (payload)=>{
    var _payload_portal;
    return useQuery([
        'useGetAllowedWidgetsForPageType',
        payload === null || payload === void 0 ? void 0 : (_payload_portal = payload.portal) === null || _payload_portal === void 0 ? void 0 : _payload_portal.customerPortalId,
        payload === null || payload === void 0 ? void 0 : payload.pageType
    ], ()=>CustomerPortalApi.getAllowedWidgetsForPageType(payload));
};
export const useCustomerPortalProjects = (projectId)=>{
    return useQuery([
        'customer-portal-projects',
        projectId
    ], async ()=>{
        if (!projectId) {
            return [];
        }
        return CustomerPortalApi.getCustomerPortalProjects({
            projectId
        });
    }, {
        enabled: projectId !== undefined && projectId !== -1
    });
};
export const useGetPortalProjectMapping = (projectId)=>{
    return useQuery([
        'useGetPortalProjectMapping',
        projectId
    ], ()=>CustomerPortalApi.getPortalProjectMapping(projectId), {
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true
    });
};
/**
 * Custom hook to fetch and process allowed domains for customer portal validation
 * @param projectId - The project ID to fetch allowed domains for
 * @returns allowedDomains array, loading state, and validation flag
 */ export const useAllowedDomains = (projectId)=>{
    var _ref;
    const { data: portalMapping, isLoading: isLoadingPortalMapping, isError } = useGetPortalProjectMapping(projectId);
    const { data: inviteSettings, isLoading: isLoadingInviteSettings } = useGetInviteSettings();
    // When invite method is "Anyone with the link", skip domain validation entirely
    const isAnyoneWithTheLink = (inviteSettings === null || inviteSettings === void 0 ? void 0 : inviteSettings.inviteMethod) === CustomerPortalInviteMethod.AnyoneWithTheLink;
    const allowedDomains = isAnyoneWithTheLink ? [] : (_ref = portalMapping === null || portalMapping === void 0 ? void 0 : portalMapping.allowedDomains) !== null && _ref !== void 0 ? _ref : [];
    // Consider domains "loaded" if:
    // 1. Query succeeded (portalMapping is defined), OR
    // 2. Query failed (isError is true) - in this case, allow all domains (no restrictions)
    // 3. Invite method is "Anyone with the link" - invite settings must also be loaded
    const hasLoadedDomains = !isLoadingInviteSettings && (isAnyoneWithTheLink || !isLoadingPortalMapping && (portalMapping !== undefined || isError));
    return {
        allowedDomains,
        hasLoadedDomains,
        isLoading: isLoadingPortalMapping || isLoadingInviteSettings,
        isError
    };
};
export const useUpdateAllowedDomainsForProject = ()=>{
    const queryClient = useQueryClient();
    return useMutation((p)=>{
        return ProjectAPI.updateAllowedDomains(p.projectId, p === null || p === void 0 ? void 0 : p.payload);
    }, {
        onSuccess: (_data, variables)=>{
            // Invalidate the portal-project-mapping query to refetch updated domains
            // This will invalidate any queries that start with this key pattern
            queryClient.invalidateQueries({
                queryKey: [
                    'useGetPortalProjectMapping',
                    variables.projectId
                ]
            });
        }
    });
};
export const useAddUsersFromShareUrl = ()=>{
    return useMutation((p)=>ProjectAPI.addUsersFromShareUrl(p));
};
export const useGetDynamicConditionFields = ()=>{
    return useQuery([
        'dynamicConditionFields'
    ], ()=>CustomerPortalApi.getDynamicConditionFields());
};
export const useDynamicConditionFilterableFieldsForCustomerPortal = ()=>{
    const { data: fields = [] } = useGetDynamicConditionFields();
    const filterableFields = useMemo(()=>getDynamicConditionFields(fields.map((f)=>({
                ...f !== null && f !== void 0 ? f : {},
                customField: false
            })), false), [
        fields
    ]);
    return filterableFields;
};
export const useGetSmartfillFieldsForCustomerPortal = (params)=>{
    return useQuery([
        'useGetSmartfillFieldsForCustomerPortal',
        params
    ], ()=>params ? CustomerPortalApi.getVariableFields(params) : Promise.resolve({
            variableFields: [],
            nestedFields: []
        }));
};
export const useGetProjectsPortalCount = (portalId)=>{
    return useQuery([
        'useGetProjectsPortalCount',
        portalId
    ], ()=>!portalId ? undefined : CustomerPortalApi.getProjectsCount(portalId));
};
export const useIsRedirectEnabled = ()=>{
    return useQuery([
        'isRedirectEnabled'
    ], ()=>CustomerPortalSettingsApi.isRedirectEnabled());
};
export const useGetCountOfTasksInProjectFromView = (projectId, viewId)=>{
    return useQuery([
        'useGetCountOfTasksInProjectFromView',
        projectId,
        viewId
    ], ()=>ProjectAPI.getCountOfTasksInProjectFromView(projectId, viewId), {
        enabled: projectId !== undefined && projectId !== -1 && viewId !== -1
    });
};
